import { useEffect, useState } from "react";
import { listAssignableAgents } from "@/api/agentRoutingApi";
import { isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   currentAssignee: string | null | undefined,
 *   conversationId?: string | null,
 *   workspaceId?: string | null,
 *   transferChat: (agentIdOrName: number | string) => Promise<void>,
 *   onAfterTransfer?: () => void,
 * }} props
 */
export default function TransferChatDialog({
  open,
  onClose,
  currentAssignee,
  conversationId,
  workspaceId,
  transferChat,
  onAfterTransfer,
}) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isLive = isHumanAgentInboxLive();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await listAssignableAgents({
          excludeAssignee: currentAssignee ?? "",
          excludeSessionId: isLive ? (conversationId ?? undefined) : undefined,
          workspaceId: isLive ? (workspaceId ?? undefined) : undefined,
        });
        if (!cancelled) setAgents(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentAssignee, conversationId, workspaceId, isLive]);

  if (!open) return null;

  const handlePick = async (agent) => {
    setBusy(true);
    setError(null);
    try {
      // In live mode pass the agent's numeric id; in stub mode pass the display name.
      const arg = isLive ? agent.id : agent.name;
      await transferChat(arg);
      onAfterTransfer?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="transfer-chat-title">
      <button type="button" className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" aria-label="Close transfer dialog" onClick={() => !busy && onClose()} />
      <div className="relative z-[1] w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 id="transfer-chat-title" className="text-base font-semibold text-slate-900">
            Transfer conversation
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Assign this chat to another teammate. Online agents are listed first; Offline agents can still receive a manual transfer.
          </p>
        </div>
        <div className="max-h-[min(60vh,320px)] overflow-y-auto px-3 py-2">
          {loading && <p className="px-2 py-6 text-center text-sm text-slate-500">Loading agents…</p>}
          {!loading && error && <p className="px-2 py-3 text-center text-sm text-rose-700">{error.message}</p>}
          {!loading && !error && agents.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-slate-500">No other agents are available for transfer.</p>
          )}
          {!loading &&
            !error &&
            [...agents]
              .sort((a, b) => Number(Boolean(b.is_online)) - Number(Boolean(a.is_online)))
              .map((a) => (
              <button
                key={a.id}
                type="button"
                disabled={busy}
                onClick={() => void handlePick(a)}
                className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 transition-colors hover:bg-teal-50 disabled:opacity-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {isLive && (
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${a.is_online ? "bg-emerald-500" : "bg-slate-300"}`}
                      aria-hidden
                    />
                  )}
                  <span className="truncate">{a.name}</span>
                </span>
                {isLive ? (
                  <span
                    className={`shrink-0 text-[11px] font-semibold ${
                      a.is_online ? "text-emerald-700" : "text-slate-400"
                    }`}
                  >
                    {a.is_online ? "Online" : "Offline"}
                  </span>
                ) : (
                  a.maxConcurrentChats != null && (
                    <span className="text-[11px] font-normal text-slate-400">max {a.maxConcurrentChats} open</span>
                  )
                )}
              </button>
            ))}
        </div>
        <div className="border-t border-slate-100 px-5 py-3">
          <button type="button" disabled={busy} className="w-full rounded-xl py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
